import { useDispatch, useSelector } from 'react-redux';
import { Box, Icon, Text } from '@quintoandar-tokko/jopijs';
import Funnel from '@/components/Funnel';
import Trans from '@/components/shared/Trans';
import translations from '@/components/Dashboard/Dashboard.trans.json';
import styles from './conversionRate.module.scss';
import PerformanceCard from '../PerformanceCard';
import useFetchDashboard from '../../customHooks/useFetchDashboard';
import { getPerformanceConversionRateCard } from '@/store/slices/components/dashboard/service';
import { setPerformanceModals } from '@/store/slices/components/dashboard/DashboardSlice';

export default function ConversionRate() {
  const dispatch = useDispatch();
  const {
    loading,
    hasFetched,
    rate: mainRate,
    percentage_status,
    variation,
    icon,
    funnel,
    losses,
  } = useSelector((state) => state.DashboardSlice.conversionRate);
  const { ref, isStale, useFetchOnViewport } = useFetchDashboard();

  const colors = [
    '#5E92A6',
    '#427F94',
    '#367083',
    'var(--colors-brand-secondary-700)',
  ];
  const rates = [
    {
      id: 1,
      percentage: funnel.visits_percentage,
      text: Trans(
        translations,
        'CountNewContacts',
        String(funnel.new_contacts)
      ),
    },
    {
      id: 2,
      percentage: funnel.reservations_percentage,
      text: Trans(
        translations,
        'CountHaveMadeVisits',
        String(funnel.visits_count)
      ),
    },
    {
      id: 3,
      percentage: funnel.concreted_percentage,
      text: Trans(
        translations,
        'CountHaveReserved',
        String(funnel.reservations_count)
      ),
    },
    {
      id: 4,
      percentage: null,
      text: Trans(
        translations,
        'CountHaveConcreted',
        String(funnel.concreted_count)
      ),
    },
  ];

  useFetchOnViewport(
    (query) => {
      dispatch(getPerformanceConversionRateCard({ query }));
    },
    [],
    { hasFetched }
  );

  const getChipIdealTextLength = () => {
    const textLength = `${
      Math.max(
        funnel.visits_percentage.length,
        funnel.reservations_percentage.length,
        funnel.concreted_percentage.length
      ) + 1
    }ch`;
    const gap = '2px';

    return `calc(${textLength} + ${gap})`;
  };

  const handleOpenTable = () => {
    dispatch(
      setPerformanceModals({
        key: 'conversionRate',
        subkey: 'isOpen',
        value: true,
      })
    );
  };

  return (
    <PerformanceCard
      ref={ref}
      loading={loading || isStale}
      id="conversion-rate"
    >
      <PerformanceCard.Header handleClickTableIcon={handleOpenTable}>
        {Trans(translations, 'ConversionRate')}
      </PerformanceCard.Header>

      <PerformanceCard.Summary
        value={mainRate}
        tendency={variation}
        icon={icon}
        percentageStatus={percentage_status}
      />

      <PerformanceCard.Content>
        <Box className={styles.conversionRate}>
          <Box className={styles.funnelsContainer}>
            {rates.map((rate, index) => (
              <Box key={rate.id} className={styles.funnelWrapper}>
                <Funnel width="100%" color={colors[index % colors.length]} />
                <Text className={styles.funnelText}>{rate.text}</Text>

                {rate.percentage && (
                  <Box className={styles.funnelChip}>
                    <Icon
                      icon="icon-flechaabajo"
                      className={styles.funnelChipIcon}
                    />
                    <Text
                      className={styles.funnelChipText}
                      style={{ width: getChipIdealTextLength() }}
                    >
                      {rate.percentage}
                    </Text>
                  </Box>
                )}
              </Box>
            ))}
          </Box>
          <Box className={styles.totalContainer}>
            <Box className={styles.totalChip}>
              <Icon icon="icon-flechaabajo" className={styles.funnelChipIcon} />
              <Text className={styles.funnelChipText}>{losses.percentage}</Text>
            </Box>
            <Text className={styles.totalLost}>
              {Trans(translations, 'CountTotalLost', String(losses.count))}
            </Text>
          </Box>
        </Box>
      </PerformanceCard.Content>
    </PerformanceCard>
  );
}
